import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { ChartBar, Printer } from '@phosphor-icons/react';
import { shizaiApi, yen } from './shizaiApi';

const METHOD_COLORS = {
  techs: 'bg-emerald-500', direct: 'bg-sky-500', license: 'bg-violet-500',
  rental: 'bg-amber-500', undecided: 'bg-zinc-400',
};

/** 調達方法別 購買サマリー — 入荷実績（実際購入金額）ベースの年間集計。 */
export default function ProcurementReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [packMonths, setPackMonths] = useState(null);   // {months:[], missing_date_count}
  const [packMonth, setPackMonth] = useState(null);     // 1-12

  const openPack = () => {
    if (!packMonth) return;
    window.open(`/shizai/invoice-pack?year=${year}&month=${packMonth}`,
      '_blank', 'noopener,noreferrer');
  };

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      setData(await shizaiApi.procurementReport(y));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'レポートの取得に失敗しました');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(year); }, [load, year]);

  // どの月にデータがあるか先に取る。空の月を印刷させても白紙が出るだけで分からない。
  useEffect(() => {
    let alive = true;
    setPackMonths(null); setPackMonth(null);
    shizaiApi.invoicePackMonths(year).then((d) => {
      if (!alive) return;
      setPackMonths(d);
      const withData = (d.months || []).filter((m) => m.count > 0);
      setPackMonth(withData.length ? withData[withData.length - 1].month : null);
    }).catch(() => { if (alive) setPackMonths({ months: [], missing_date_count: 0 }); });
    return () => { alive = false; };
  }, [year]);

  const years = data?.years?.length ? data.years : [new Date().getFullYear()];
  const maxAmount = Math.max(1, ...(data?.methods || []).map((m) => m.amount));

  return (
    <div className="max-w-3xl space-y-4" data-testid="procurement-report">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold text-zinc-800 flex items-center gap-2">
          <ChartBar size={18} weight="duotone" className="text-sky-600" />
          調達方法別 購買サマリー
        </p>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-800 outline-none focus:ring-2 focus:ring-sky-200"
          data-testid="report-year-select">
          {(years.includes(year) ? years : [year, ...years]).map((y) => (
            <option key={y} value={y}>{`${y}年`}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="py-16 text-center text-sm text-zinc-400">読み込み中...</p>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4">
            <p className="text-xs text-zinc-500">{year}年 入荷実績合計（実際購入金額）</p>
            <p className="text-2xl font-bold text-zinc-900 tabular-nums mt-0.5" data-testid="report-grand-total">
              {yen(data?.grand_total)}
            </p>
            <p className="text-[11px] text-zinc-400 mt-0.5">{data?.request_count || 0}件の入荷済み依頼</p>
          </div>

          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 space-y-3">
            {(data?.methods || []).map((m) => (
              <div key={m.method} data-testid={`report-method-${m.method}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-zinc-800 flex items-center gap-1.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${METHOD_COLORS[m.method]}`} />
                    {m.label}
                    <span className="text-[11px] text-zinc-400">{m.count}件</span>
                  </span>
                  <span className="font-semibold tabular-nums text-zinc-900" data-testid={`report-amount-${m.method}`}>
                    {yen(m.amount)}
                  </span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-zinc-100 overflow-hidden">
                  <div className={`h-full rounded-full ${METHOD_COLORS[m.method]} transition-all`}
                    style={{ width: `${Math.round((m.amount / maxAmount) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-zinc-400">
            ※ 入荷確認・入荷スキャンで記録された実際購入金額を、入荷日ベースで集計しています。
            調達方法が未設定の依頼は「未定」に含まれます。
          </p>

          {/* 月次 請求書パック — TECHS外（直接購入/ライセンス/レンタル）を請求書日付で束ねて印刷 */}
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 flex flex-wrap items-end gap-3"
            data-testid="invoice-pack-launcher">
            <div>
              <p className="text-sm font-semibold text-zinc-800">月次 請求書パック（TECHS外 直接購入）</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                直接購入・ライセンス・レンタルの請求書を、入荷確認で入れた請求書日付の月ごとに
                表紙＋添付でまとめて印刷します。請求書日付が未設定の購入は要修正リストに出ます。
              </p>
              {packMonths && (
                <p className="mt-1 text-[11px]" data-testid="invoice-pack-month-hint">
                  <span className="text-zinc-500">
                    {year}年にデータがある月:{' '}
                    {(packMonths.months || []).filter((m) => m.count > 0).length
                      ? (packMonths.months || []).filter((m) => m.count > 0)
                        .map((m) => `${m.month}月`).join('・')
                      : 'なし'}
                  </span>
                  {packMonths.missing_date_count > 0 && (
                    <span className="ml-2 font-semibold text-amber-700">
                      請求書日付が未設定の購入 {packMonths.missing_date_count}件（どの月にも入りません）
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="ml-auto flex items-end gap-2">
              <span className="flex flex-col gap-1">
                <span className="text-[11px] text-zinc-500">対象の月（データがある月だけ選べます）</span>
                <select value={packMonth ?? ''} disabled={!packMonths}
                  onChange={(e) => setPackMonth(Number(e.target.value) || null)}
                  className="h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-800 outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-zinc-50"
                  data-testid="invoice-pack-month">
                  {!packMonths && <option value="">読み込み中…</option>}
                  {packMonths && (packMonths.months || []).filter((m) => m.count > 0).length === 0 && (
                    <option value="">{year}年はデータがありません</option>
                  )}
                  {(packMonths?.months || []).filter((m) => m.count > 0).map((m) => (
                    <option key={m.month} value={m.month}>
                      {`${m.month}月 — ${m.count}件 / ${yen(m.total_amount)}`}
                      {m.no_file_count > 0 ? `（請求書未添付 ${m.no_file_count}件）` : ''}
                    </option>
                  ))}
                </select>
              </span>
              <button type="button" onClick={openPack} disabled={!packMonth}
                className="h-9 px-3 rounded-md bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 flex items-center gap-1.5"
                data-testid="invoice-pack-open">
                <Printer size={15} weight="bold" /> 請求書パックを印刷
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
